import React, { useContext, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FiCheck, FiChevronLeft, FiLock, FiTruck } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import "../css/checkout.css";

const initialForm = {
  contact: "",
  country: "Mexico",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  apartment: "",
  city: "",
  state: "",
  postalCode: "",
};

export const supportedCountries = [
  "Mexico",
  "Australia",
  "Austria",
  "Belgium",
  "Canada",
  "Denmark",
  "Finland",
  "France",
  "Germany",
  "Ireland",
  "Italy",
  "Japan",
  "Luxembourg",
  "Malaysia",
  "Netherlands",
  "New Zealand",
  "Norway",
  "Portugal",
  "Singapore",
  "South Korea",
  "Spain",
  "Sweden",
  "Switzerland",
  "Taiwan",
  "Thailand",
  "United Arab Emirates",
  "United Kingdom",
  "United States",
];

export const buildPaymentUrl = ({ origin, orderNumber, amount, currency, email, title }) => {
  const paymentUrl = new URL("/create_payment.php", origin);
  paymentUrl.searchParams.set("orderNo", orderNumber);
  paymentUrl.searchParams.set("amount", Number(amount).toFixed(2));
  paymentUrl.searchParams.set("currency", currency);
  paymentUrl.searchParams.set("email", email.trim());
  paymentUrl.searchParams.set("success_url", `${origin}/payment?state=success`);
  paymentUrl.searchParams.set("cancel_url", `${origin}/payment?state=cancel`);
  paymentUrl.searchParams.set("title", title);
  return paymentUrl;
};

const CheckOut = () => {
  const { cartItems } = useContext(CartContext);
  const { state } = useLocation();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  const detailedItems = useMemo(
    () =>
      cartItems
        .map((item) => ({ ...item, product: productData.find((product) => product.id === item.id) }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = detailedItems.reduce(
    (total, item) => total + Number(item.product.price) * item.quantity,
    0
  );
  const demoPromoCode = siteConfig.checkout?.demoPromoCode || "CLARITY10";
  const demoDiscountRate = siteConfig.checkout?.demoDiscountRate ?? 0.1;
  const discountRate =
    state?.promoCode === demoPromoCode &&
    Number(state?.discountRate) === demoDiscountRate
      ? demoDiscountRate
      : 0;
  const discount = subtotal * discountRate;
  const shipping = siteConfig.shipping?.standardRate ?? 9.99;
  const total = subtotal - discount + shipping;

  const money = (value) =>
    new Intl.NumberFormat(siteConfig.currency?.locale || "en-US", {
      style: "currency",
      currency: siteConfig.currency?.code || "USD",
    }).format(Number(value));

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
    if (errors[name]) setErrors((previous) => ({ ...previous, [name]: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    const required = ["contact", "country", "firstName", "lastName", "address", "city", "state", "postalCode"];
    required.forEach((field) => {
      if (!form[field].trim()) nextErrors[field] = "This field is required.";
    });
    if (form.contact && !/^\S+@\S+\.\S+$/.test(form.contact)) nextErrors.contact = "Enter a valid email address.";
    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      const field = document.querySelector(`[name="${Object.keys(nextErrors)[0]}"]`);
      field?.focus();
      field?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setIsProcessing(true);
    const randomPart = window.crypto?.getRandomValues
      ? window.crypto.getRandomValues(new Uint32Array(1))[0].toString().padStart(10, "0")
      : Math.floor(Math.random() * 10 ** 10).toString().padStart(10, "0");
    const orderNumber = `${Date.now()}${randomPart}`;
    const orderDetails = { orderNumber, total: money(total), email: form.contact };
    try {
      window.sessionStorage.setItem(
        siteConfig.checkout?.lastOrderStorageKey || "storefrontLastOrder",
        JSON.stringify(orderDetails)
      );
    } catch {
      // The payment redirect still works when session storage is unavailable.
    }

    const paymentUrl = buildPaymentUrl({
      origin: window.location.origin,
      orderNumber,
      amount: total,
      currency: siteConfig.currency?.code || "USD",
      email: form.contact,
      title: detailedItems[0].product.name,
    });
    window.location.assign(paymentUrl.toString());
  };

  const field = (name, label, options = {}) => (
    <label className={`checkout-field ${errors[name] ? "has-error" : ""}`}>
      <span>{label}</span>
      <input
        type={options.type || "text"}
        name={name}
        value={form[name]}
        onChange={updateField}
        autoComplete={options.autoComplete}
        inputMode={options.inputMode}
        placeholder={options.placeholder || label}
        aria-invalid={Boolean(errors[name])}
        aria-describedby={errors[name] ? `${name}-error` : undefined}
      />
      {errors[name] && <small id={`${name}-error`}>{errors[name]}</small>}
    </label>
  );

  if (detailedItems.length === 0) {
    return (
      <main className="checkout-empty" id="main-content">
        <FiLock />
        <p className="eyebrow">SECURE CHECKOUT</p>
        <h1>Your cart is ready for a new beginning.</h1>
        <p>Add an item before continuing to checkout.</p>
        <Link className="button button--dark" to="/shop">RETURN TO SHOP</Link>
      </main>
    );
  }

  return (
    <main className="checkout-page" id="main-content">
      <div className="checkout-banner"><FiLock /> SECURE CHECKOUT · ENCRYPTED PAYMENT</div>
      <div className="checkout-layout">
        <form className="checkout-form" onSubmit={handleSubmit} noValidate>
          <div className="checkout-form__inner">
            <Link className="checkout-back" to="/cartpage"><FiChevronLeft /> RETURN TO CART</Link>
            <h1>CHECKOUT</h1>

            <section className="checkout-section">
              <div className="checkout-section__heading"><span>01</span><div><h2>CONTACT</h2><p>Your receipt and delivery updates will be sent here.</p></div></div>
              {field("contact", "Email address", { type: "email", autoComplete: "email" })}
            </section>

            <section className="checkout-section">
              <div className="checkout-section__heading"><span>02</span><div><h2>DELIVERY</h2><p>Enter the address where your order should arrive.</p></div></div>
              <label className="checkout-field">
                <span>Country / Region</span>
                <select name="country" value={form.country} onChange={updateField} autoComplete="country-name">
                  {supportedCountries.map((country) => <option key={country}>{country}</option>)}
                </select>
              </label>
              <div className="checkout-row">{field("firstName", "First name", { autoComplete: "given-name" })}{field("lastName", "Last name", { autoComplete: "family-name" })}</div>
              {field("company", "Company (optional)", { autoComplete: "organization" })}
              {field("address", "Address", { autoComplete: "address-line1" })}
              {field("apartment", "Apartment, suite, etc. (optional)", { autoComplete: "address-line2" })}
              <div className="checkout-row checkout-row--three">{field("city", "City", { autoComplete: "address-level2" })}{field("state", "State / province", { autoComplete: "address-level1" })}{field("postalCode", "Postal code", { autoComplete: "postal-code" })}</div>
            </section>

            <section className="checkout-section">
              <div className="checkout-section__heading"><span>03</span><div><h2>SHIPPING METHOD</h2><p>A single tracked rate applies to every supported destination.</p></div></div>
              <div className="checkout-shipping-options">
                <label><input type="radio" name="shipping" value="standard" checked readOnly /><FiTruck /><span><strong>Tracked standard shipping</strong><small>{siteConfig.shipping?.standardDelivery}</small></span><b>{money(shipping)}</b></label>
              </div>
            </section>

            <section className="checkout-section">
              <div className="checkout-section__heading"><span>04</span><div><h2>PAYMENT</h2><p>Card details are collected securely on the payment provider page.</p></div></div>
              <div className="checkout-payment">
                <div className="checkout-payment__heading"><span>SECURE PAYMENT REDIRECT</span><div><b>VISA</b><b>MC</b><b>AMEX</b></div></div>
                <div className="checkout-payment__body">
                  <p>You will continue to our encrypted payment partner after selecting Pay Now. No card number or security code is entered or stored on this site.</p>
                </div>
              </div>
            </section>

            <button type="submit" className="checkout-pay" disabled={isProcessing}>
              {isProcessing ? "REDIRECTING…" : `PAY NOW · ${money(total)}`}
            </button>
            <p className="checkout-legal">By placing your order, you agree to our <Link to="/terms">Terms</Link> and acknowledge our <Link to="/privacy">Privacy Policy</Link>.</p>
          </div>
        </form>

        <aside className="checkout-summary">
          <div className="checkout-summary__inner">
            <p className="eyebrow">YOUR ORDER</p>
            <div className="checkout-summary__items">
              {detailedItems.map((item) => (
                <article key={`${item.id}-${item.size}-${item.color}`}>
                  <div><img src={item.product.image} alt={item.product.name} /><span>{item.quantity}</span></div>
                  <div><h2>{item.product.name}</h2><p>{[item.size, item.color].filter(Boolean).join(" · ")}</p></div>
                  <strong>{money(Number(item.product.price) * item.quantity)}</strong>
                </article>
              ))}
            </div>
            <dl>
              <div><dt>Subtotal</dt><dd>{money(subtotal)}</dd></div>
              {discount > 0 && <div><dt>{demoPromoCode} discount</dt><dd>−{money(discount)}</dd></div>}
              <div><dt>Shipping</dt><dd>{money(shipping)}</dd></div>
            </dl>
            {state?.giftNote && <p className="checkout-summary__gift-note">Gift note: {state.giftNote}</p>}
            <div className="checkout-summary__total"><span>TOTAL <small>{siteConfig.currency?.code}</small></span><strong>{money(total)}</strong></div>
            <div className="checkout-summary__note"><FiCheck /><p><strong>Thoughtfully packed.</strong><br />Every order is wrapped with recyclable materials and prepared in {siteConfig.shipping?.processingTime}.</p></div>
            <nav><Link to="/returns">Returns</Link><Link to="/shipping">Shipping</Link><Link to="/privacy">Privacy</Link><Link to="/contact">Contact</Link></nav>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default CheckOut;
